import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import Dialog from "../Dialog";
import { Assets } from "../../constants/assets";
import { useTour } from "../../components/Tour/TourProvider";
import Button from "../Button";
import Network, { IsBeta } from "../../utils/Network";
import { QuestState } from "../../types/quests/indext";
import { useDispatch, useSelector } from "react-redux";
import { roundFloat } from "../../utils/round-float";
import { clearTour } from "../../redux/tour/action";
import { PlayerEventQuestRewardsResponse, EventQuestRewardItem } from "./types";
import useToast from "../../hooks/useToast";
import { useLocation } from "react-router-dom";
import {
  startTourWithProperPositioning,
  debouncedTourRecalculation,
} from "../../utils/tour-positioning";
import EventBus, { EventNames } from "../../game-engine/utils/event-bus";
import { useAppTranslation } from "../../i18n/useAppTranslation";

type Props = {
  tourId?: string; // Add optional tourId prop
};

// Helper functions for localStorage management
const getModalDismissalKey = (tourId: string) =>
  `arena_tour_dismissed_${tourId}`;
const DISMISSAL_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds

const setModalDismissed = (tourId: string) => {
  const timestamp = Date.now();
  localStorage.setItem(getModalDismissalKey(tourId), timestamp.toString());
};

const isModalRecentlyDismissed = (tourId: string): boolean => {
  const dismissedTimestamp = localStorage.getItem(getModalDismissalKey(tourId));
  if (!dismissedTimestamp) return false;

  const timeElapsed = Date.now() - parseInt(dismissedTimestamp, 10);
  return timeElapsed < DISMISSAL_TIMEOUT;
};

const clearModalDismissalState = (tourId: string) => {
  localStorage.removeItem(getModalDismissalKey(tourId));
};

const ArenaTour = (props: Props) => {
  const { t } = useAppTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [checkboxValue, setCheckboxValue] = useState(false);
  const { completeTour, startTour, isTourActive } = useTour();
  const dispatch = useDispatch();
  const location = useLocation();
  const openToast = useToast();

  // Get tour state from Redux to prevent unwanted auto-start
  const { tourRunStates, activeTour, completedTours } = useSelector(
    (state: any) => state.tour
  );
  const userToken = useSelector((state: any) => state.userToken);
  // Default to "arenaTour" if no tourId prop is provided
  const tourId = props.tourId || "arenaTour";
  // Check if any tour is currently running - memoized for stability
  const isAnyTourRunning = useMemo(() => {
    if (activeTour) return true;
    if (tourRunStates) {
      return Object.values(tourRunStates).some((state) => !!state);
    }
    return false;
  }, [activeTour, tourRunStates]);

  // Check if this specific tour is already completed
  const isTourCompleted = completedTours?.[tourId];

  const axios = useMemo(() => new Network(), []); // Memoize axios instance
  const [relatedQuest, setRelatedQuest] =
    useState<PlayerEventQuestRewardsResponse>();
  const [questStateIsOpen, setQuestStateIsOpen] = useState(false);
  const [hasCheckedQuest, setHasCheckedQuest] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const isNewUserArenaQuest =
    relatedQuest?.quest.eventQuestKey === "IsNewUserActive" ||
    relatedQuest?.quest.title === "New player Arena Quest";

  const getQuestTitle = () => {
    if (isNewUserArenaQuest) {
      return t("tour.arenaQuest.title", {
        defaultValue: relatedQuest.quest.title,
      });
    }

    return relatedQuest?.quest.title;
  };

  const getQuestDescription = () => {
    if (isNewUserArenaQuest) {
      return t("tour.arenaQuest.description", {
        defaultValue: relatedQuest.quest.description,
      });
    }

    return relatedQuest?.quest.description;
  };

  const getRewardName = (name?: string) => {
    if (!name) return "";

    return t(`troops.names.${name.toLowerCase()}`, {
      defaultValue: name,
    });
  };

  // Use refs to prevent infinite loops in useEffect
  const isCheckingQuestRef = useRef(false);
  const questCheckedCountRef = useRef(0);
  const lastQuestCheckTimeRef = useRef(0);

  // Function to reset all tour states and conditions
  const resetTourStates = useCallback(() => {
    // Reset all tour states
    dispatch(clearTour());

    // Explicitly ensure the tour is not running
    dispatch({
      type: "SET_TOUR_RUN_STATE",
      payload: { tourId, running: false },
    });

    // Ensure no active tour is set
    dispatch({
      type: "SET_ACTIVE_TOUR",
      payload: null,
    });

    // Reset component-level states that prevent initial runs
    setHasCheckedQuest(false);
    setQuestStateIsOpen(false);
    setRelatedQuest(undefined);

    // Reset refs that track quest checking - allow fresh checks after reset
    questCheckedCountRef.current = 0;
    lastQuestCheckTimeRef.current = 0;
    isCheckingQuestRef.current = false;

    // Clear dismissal state to allow modal to show again
    clearModalDismissalState(tourId);
  }, [dispatch, tourId]);

  // Clear any ongoing tour state when the component mounts and prevent auto-starting
  useEffect(() => {
    resetTourStates();
  }, [resetTourStates]);

  // Periodic counter reset to prevent permanent lock-out from quest checks
  useEffect(() => {
    const resetInterval = setInterval(() => {
      // Don't reset if user recently dismissed - respect their choice
      const wasRecentlyDismissed = isModalRecentlyDismissed(tourId);
      if (!wasRecentlyDismissed && questCheckedCountRef.current > 5) {
        questCheckedCountRef.current = 0;
        lastQuestCheckTimeRef.current = 0;
      }
    }, 120000); // 2 minutes

    return () => {
      clearInterval(resetInterval);
    };
  }, [tourId]);

  useEffect(() => {
    if (location.pathname === "/home" && !isOpen) {
      resetTourStates();
      handleQuestStatus();
    }
  }, [location.pathname]);
  // Stabilized quest checking function with memoized dependencies
  const checkArenaStatus = useCallback(async () => {
    // Prevent multiple concurrent calls
    if (isCheckingQuestRef.current) {
      return { isQuestOpen: false };
    }

    // Rate limit quest checking to prevent excessive API calls
    const now = Date.now();
    if (now - lastQuestCheckTimeRef.current < 2000) {
      // 2 second rate limit
      return { isQuestOpen: false };
    }

    try {
      isCheckingQuestRef.current = true;
      lastQuestCheckTimeRef.current = now;

      const response = await axios.CallApi<PlayerEventQuestRewardsResponse>({
        url: "/quest/playereventquests?eventQuestKey=IsNewUserActive",
        method: "GET",
      });
      const relatedQ = response?.data?.[0];
      // Parse rewards if they are in string format
      const parsedQuest = relatedQ
        ? {
            ...relatedQ,
            quest: {
              ...relatedQ.quest,
              rewards:
                typeof relatedQ.quest.rewards === "string"
                  ? JSON.parse(relatedQ.quest.rewards)
                  : relatedQ.quest.rewards,
            },
          }
        : null;

      const isQuestOpen =
        parsedQuest &&
        parsedQuest.quest &&
        parsedQuest.state !== QuestState.Claimed &&
        parsedQuest.state !== QuestState.Expired;

      // Set the related quest state directly here
      if (parsedQuest) {
        setRelatedQuest(parsedQuest);
      }

      return { isQuestOpen };
    } catch (error) {
      return { isQuestOpen: false };
    } finally {
      isCheckingQuestRef.current = false;
    }
  }, [axios]); // Only depend on stable references

  const handleEventQuestClaim = useCallback(async () => {
    try {
      setIsClaiming(true);
      const response = await axios.CallApi({
        url: "/quest/eventquests/claim",
        method: "POST",
        body: {
          questId: relatedQuest?.questId,
        },
      });
      openToast(response);
      if (response.ok) {
        dispatch(clearTour());
        // Clear dismissal state when quest is claimed successfully
        clearModalDismissalState(tourId);
        // Reset quest check counter to allow fresh checks after claiming
        questCheckedCountRef.current = 0;
        setIsOpen(false);
        window.location.reload();
      }
    } catch (error) {
      console.error("Error claiming event quest:", error);
    } finally {
      setIsClaiming(false);
    }
  }, [relatedQuest, tourId, axios, openToast, dispatch]);

  // Stabilized quest status handler with proper dependency management
  const handleQuestStatus = useCallback(async () => {
    if (!userToken || window.location.pathname.includes("login")) return;

    const { isQuestOpen } = await checkArenaStatus();

    // Reset counter when quest state changes meaningfully
    if (isQuestOpen !== questStateIsOpen) {
      questCheckedCountRef.current = 0;
    }

    // Update our state only if values actually changed
    setQuestStateIsOpen((prev) => (prev !== isQuestOpen ? isQuestOpen : prev));
    setHasCheckedQuest((prev) => (!prev ? true : prev));

    // ONLY show the modal dialog, NEVER auto-start the tour
    if (
      isQuestOpen &&
      !isAnyTourRunning &&
      !isOpen && // Also check if modal is not already open
      !doNotShowAgain // Don't show if user chose not to show again this session
    ) {
      // IMPORTANT: Only open the modal - DO NOT start the tour automatically
      setIsOpen(true);
    } else {
      // If the quest is not open but a tour is running, close the tour
      if (!isQuestOpen && isTourActive(tourId)) {
        // Close the tour gracefully
        completeTour(tourId);

        // Ensure it's marked as completed in Redux
        dispatch({
          type: "SET_TOUR_COMPLETED",
          payload: tourId,
        });
      }
    }
  }, [
    checkArenaStatus,
    isAnyTourRunning,
    isTourCompleted,
    isTourActive,
    tourId,
    completeTour,
    dispatch,
    questStateIsOpen,
    relatedQuest,
    userToken,
    doNotShowAgain,
    isOpen,
  ]);

  // Periodic quest status refresh
  // useEffect(() => {
  //   if (hasCheckedQuest) {
  //     const now = Date.now();
  //     const timeSinceLastCheck = now - lastQuestCheckTimeRef.current;
  //     const wasRecentlyDismissed = isModalRecentlyDismissed(tourId);

  //     // Be much more conservative if user dismissed the modal
  //     const minimumCheckInterval = wasRecentlyDismissed ? 30000 : 5000; // 30s vs 5s

  //     if (!relatedQuest || timeSinceLastCheck > minimumCheckInterval) {
  //       handleQuestStatus();
  //     }
  //   }
  // }, [hasCheckedQuest, relatedQuest, handleQuestStatus, tourId]);

  // Event listener for resetting tour states
  useEffect(() => {
    const handleResetEvent = (payload: { tourId?: string }) => {
      // If no specific tourId is provided, or if it matches our tourId, reset
      if (!payload.tourId || payload.tourId === tourId) {
        resetTourStates();

        // Trigger a fresh quest check after reset
        setTimeout(() => {
          handleQuestStatus();
        }, 100);
      } else {
      }
    };

    // Listen for the reset event
    EventBus.on(EventNames.ResetArenaTour, handleResetEvent);

    // Cleanup listener on unmount
    return () => {
      EventBus.off(EventNames.ResetArenaTour, handleResetEvent);
    };
  }, [tourId, resetTourStates, handleQuestStatus]);

  // Simplified initial quest check on mount - only run once
  useEffect(() => {
    let isMounted = true;

    const runInitialCheck = async () => {
      if (isMounted && !hasCheckedQuest) {
        await handleQuestStatus();
      }
    };

    runInitialCheck();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run on mount

  const handleCloseModal = useCallback(
    (dontShowAgain = false) => {
      setIsOpen(false);

      // If user chose "do not show again", set the state for this session
      if (dontShowAgain) {
        setDoNotShowAgain(true);
      }

      location.hash = "";

      // Reset quest check counter to give the dismissal logic a fresh start
      questCheckedCountRef.current = 0;
    },
    [tourId, location]
  );

  // Simplified tour state watching with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const debouncedStateCheck = () => {
      // If any tour starts running and dialog is open, close it
      if (isAnyTourRunning && isOpen) {
        setIsOpen(false);
      }

      // If quest state is not open and this tour is active, close the tour
      if (!questStateIsOpen && isTourActive(tourId)) {
        completeTour(tourId);
        dispatch({
          type: "SET_TOUR_COMPLETED",
          payload: tourId,
        });
      }
    };

    // Debounce state checks to prevent rapid consecutive calls
    timeoutId = setTimeout(debouncedStateCheck, 100);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    isAnyTourRunning,
    questStateIsOpen,
    isTourActive,
    tourId,
    completeTour,
    dispatch,
  ]);

  const handleStartTour = useCallback(async () => {
    // Clear hash first
    window.location.hash = "";

    // CRITICAL SAFETY CHECKS - Don't start the tour if conditions aren't met
    if (!questStateIsOpen) {
      console.warn(`Tour "${tourId}" cannot start: quest is not in open state`);
      setIsOpen(false);
      return;
    }

    // Don't start if any tour is already running
    if (isAnyTourRunning) {
      console.warn(
        `Tour "${tourId}" cannot start: another tour is already running`
      );
      setIsOpen(false);
      return;
    }

    // Don't start if this specific tour is already active
    if (isTourActive(tourId)) {
      console.warn(
        `Tour "${tourId}" is already active - ignoring start request`
      );
      setIsOpen(false);
      return;
    }

    // Clear dismissal state when tour is started
    clearModalDismissalState(tourId);

    // Close dialog first
    setIsOpen(false);

    // IMPORTANT: Use direct Redux dispatch for reliable state management
    // First reset any existing tour state
    dispatch({
      type: "RESET_TOUR",
      payload: tourId,
    });

    // Set as active tour
    dispatch({
      type: "SET_ACTIVE_TOUR",
      payload: tourId,
    });

    // Set tour run state to true
    dispatch({
      type: "SET_TOUR_RUN_STATE",
      payload: { tourId, running: true },
    });

    // After Redux updates, use the enhanced tour start function with proper positioning
    // This ensures target elements are fully rendered and positioned before starting
    try {
      await startTourWithProperPositioning(
        () => {
          startTour(tourId);
          // Force a focus on the document body to ensure UI interaction works
          document.body.focus();
        },
        // Common arena tour target selectors - adjust based on your actual tour targets
        [
          '[data-tour="arena-button"]',
          '[data-tour="battle-button"]',
          '[data-tour="rankings"]',
          ".arena-stats",
          ".game-bar",
          ".troop-formation",
        ],
        {
          pageStabilityDelay: 600, // Wait a bit longer for arena page elements
          elementReadinessTimeout: 4000, // Give more time for complex elements
          finalDelay: 400, // Extra delay before recalculation
        }
      );
    } catch (error) {
      console.warn(
        "Enhanced tour start failed, falling back to basic approach:",
        error
      );
      // Fallback to original approach if enhanced method fails
      setTimeout(() => {
        startTour(tourId);
        document.body.focus();
      }, 300);
    }
  }, [
    questStateIsOpen,
    tourId,
    dispatch,
    startTour,
    isAnyTourRunning,
    isTourActive,
  ]);

  const progress = useMemo(() => {
    if (!relatedQuest) return 0;
    return (
      ((relatedQuest.progress || 0) * 100) /
        relatedQuest?.quest?.requiredActionValue || 0
    );
  }, [relatedQuest]);

  // Hash-based tour opening with stability
  useEffect(() => {
    if (location.hash === "#newUserTour" && !isOpen) {
      // Respect user dismissal even for hash-based opening
      const wasRecentlyDismissed = isModalRecentlyDismissed(tourId);
      if (!wasRecentlyDismissed) {
        setIsOpen(true);
      }
    }
  }, [location.hash, isOpen, tourId]);

  // Reset checkbox when modal opens
  useEffect(() => {
    if (isOpen) {
      setCheckboxValue(false);
    }
  }, [isOpen]);

  // Monitor for dynamic content changes and recalculate tour positions
  useEffect(() => {
    if (!isTourActive(tourId)) return;

    // Set up a mutation observer to watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      let shouldRecalculate = false;

      mutations.forEach((mutation) => {
        // Check if the mutation affects layout
        if (
          mutation.type === "childList" ||
          (mutation.type === "attributes" &&
            ["class", "style", "data-tour"].includes(
              mutation.attributeName || ""
            ))
        ) {
          shouldRecalculate = true;
        }
      });

      if (shouldRecalculate) {
        // Use debounced recalculation to avoid excessive calls
        debouncedTourRecalculation(500);
      }
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "data-tour"],
    });

    // Also listen for window resize events
    const handleResize = () => {
      if (isTourActive(tourId)) {
        debouncedTourRecalculation(300);
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    // Cleanup
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [isTourActive, tourId]);

  return (
    <Dialog
      open={isOpen}
      onClose={() => handleCloseModal(checkboxValue)} 
      title={t("common:tour.arenaPath")}
      noFooter
    >
      <div style={{ marginTop: -20 }} className="text-center bold  fs-big">
        {getQuestTitle()}
      </div>
      <div className="text-center dark-yellow mb-3 fs-small">
        {getQuestDescription()}
      </div>
      <img src={Assets.campaigns.arenaTour} className="w-100" />

      <div className="form-processbar">
        <div style={{ height: 22 }} className="progress mt-3">
          <div
            style={{
              width: progress + "%",
              height: 14,
            }}
            className="progress-bar progress-bar-striped yellow"
          >
            {relatedQuest?.progress && relatedQuest?.progress > 0 && (
              <span
                style={{ marginTop: 1 }}
                className="fs-large bold   text-dark"
              >
                {roundFloat(progress, 0) + "%"}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="d-flex mt-3  flex-nowrap  gap-1 overflow-auto">
        {relatedQuest?.quest.rewards &&
          Array.isArray(relatedQuest.quest.rewards) &&
          (relatedQuest.quest.rewards as EventQuestRewardItem[]).map(
            (r: EventQuestRewardItem, index: number) => (
              <div
                key={index}
                style={{ minWidth: 90 }}
                className="item-frame active-slot flex-column"
              >
                <img src={r.icon} width={50} />
                {r.name && (
                  <span className="dark-yellow text-center px-2 fs-tiny">
                    {getRewardName(r.name)}
                  </span>
                )}
                {r.level && (
                  <span className="dark-yellow text-center px-2 fs-tiny">
                    {r.quantity > 1 ? `${r.quantity}x` : ""}+ {t("labels.level")} {r.level}
                  </span>
                )}
                {!r.level && (
                  <span className="dark-yellow text-center px-2 fs-tiny">
                    +{r.quantity}
                  </span>
                )}
              </div>
            )
          )}
      </div>

      {/* Do not show again checkbox */}
      <div className="d-flex align-items-center justify-content-center mt-3">
        <label className="d-flex align-items-center cursor-pointer">
          <input
            type="checkbox"
            checked={checkboxValue}
            onChange={(e) => setCheckboxValue(e.target.checked)}
            className="me-2"
          />
          <span className="fs-small text-white">
            {t("tour.arenaQuest.dontShowAgainSession")}
          </span>
        </label>
      </div>

      <div
        style={{ marginBottom: -30 }}
        className="d-flex justify-content-center mt-3"
      >
        {relatedQuest?.state === QuestState.Done ? (
          <Button
            loading={isClaiming}
            onClick={handleEventQuestClaim}
            variant="success"
            disabled={!questStateIsOpen}
          >
            {t("actions.claim")}
          </Button>
        ) : (
          <Button
            onClick={handleStartTour}
            variant="primary"
            disabled={!questStateIsOpen}
          >
            {t("actions.start")}
          </Button>
        )}
      </div>
    </Dialog>
  );
};

export default ArenaTour;
